import axios from "axios";
import moment from "moment";

import { version } from "../../package.json";
import { API_URL, ESV_DETAILS } from "./Constant";

const ESV_STORAGE_KEY = "user_details";

let cachedFirebaseToken = null;

export const getCachedFirebaseToken = () => cachedFirebaseToken;

export const setCachedFirebaseToken = (token) => {
  cachedFirebaseToken = token || null;
};

// const checkIfRememberMe = () => !sessionStorage.getItem(ESV_STORAGE_KEY);

// Check if session or local storage is available (https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API)
// Safari's strict privacy settings, which can block access to localStorage in certain contexts
function storageAvailable(type) {
  let storage;
  try {
    storage = window[type]; // Access the actual storage object
    const x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get user info stored in session storage.
 * @returns {Object} user info object if found, null otherwise
 */
export const getUserCredentials = () => {
  const userInfoAsString = sessionStorage.getItem(ESV_STORAGE_KEY);
  // if (!userInfoAsString) {
  //   userInfoAsString = localStorage.getItem(ESV_STORAGE_KEY);
  // }
  const userInfo = JSON.parse(userInfoAsString) || null;

  return userInfo;
};

/**
 * Store user info in session storage.
 * @param {String} _storageType (always uses sessionStorage)
 * @param {Object} data user info
 * @param {Object} appContext app-level state
 */
export const setUserCredentials = (_storageType, data, appContext) => {
  // let storageType = _storageType;
  // if (!storageType) {
  //   storageType = checkIfRememberMe() ? "local" : "session";
  // }

  // const storage = (storageType === "local" && storageAvailable(localStorage)) ? localStorage : sessionStorage;
  const storage = sessionStorage;

  /** Update user info in app-level state */
  const setUserTitle = appContext?.setUserTitle;
  if (typeof setUserTitle === "function") {
    setUserTitle(data.user_title);
  }
  storage.setItem(ESV_STORAGE_KEY, JSON.stringify(data));
};

export const setAuthOnlyCredentials = (data, appContext) => {
  setUserCredentials("session", { ...data, authOnly: true }, appContext);
  if (data.firebase_id_token) {
    setCachedFirebaseToken(data.firebase_id_token);
  }
};

export const clearAuthOnlyCredentials = () => {
  const user = getUserCredentials();
  if (user?.authOnly) {
    sessionStorage.removeItem(ESV_STORAGE_KEY);
    setCachedFirebaseToken(null);
  }
};

export const isAuthOnlySession = () => !!getUserCredentials()?.authOnly;

/**
 * Check if user is logged in (checks local and session storage)
 * @returns {Boolean} true if user is logged in, false if not
 */
export const isUser = () => {
  const user_details = getUserCredentials();
  if (user_details?.authOnly) {
    return false;
  }
  return !!(user_details && (user_details.firebase_id_token || user_details.user_email || user_details.uid));
};

/**
 * Check if user is a third-party user (accessing via external invitation link)
 * @returns {Boolean} true if user is a third-party user, false otherwise
 */
export const isThirdPartyUser = () => {
  const user_details = getUserCredentials();
  return !!(user_details && user_details.thirdParty === true);
};

/**
 * Returns fields to be added to every api payload
 * @returns {Object} returns { user_payload, company_name, company_domain } if user is not null
 */
export const getMandatoryUserData = () => {
  const user = getUserCredentials();
  return user === null ? {} : { user_payload: user.user_payload, company_name: user.company_name, company_domain: user.company_domain };
};

let isSigningOut = false;

/**
 * Check if sign out is currently in progress
 * @returns {boolean} true if sign out is in progress
 */
export const isSignOutInProgress = () => isSigningOut;

/**
 * Sign out user by clearing storage
 * This function should be called after logout() to clear local storage and navigate to login.
 * @param {Object} history react-router history object
 * @param {Object} appContext app-level state
 */
export const signOut = (history, appContext) => {
  // Prevent concurrent signOut calls
  if (isSigningOut) {
    return;
  }

  // Validate required parameters
  if (!history || !appContext) {
    console.error("signOut: history and appContext are required"); // eslint-disable-line no-console
    return;
  }

  isSigningOut = true;

  try {
    const userCredentials = getUserCredentials();
    // Check if user is a support user by checking uid or user_email
    const isSupportUser = userCredentials?.uid === "support" || userCredentials?.user_email === "support";

    // Clear cached Firebase token first
    setCachedFirebaseToken(null);

    // Clear storage with availability checks
    // if (checkIfRememberMe()) {
    //   if (storageAvailable("localStorage")) {
    //     localStorage.removeItem(ESV_STORAGE_KEY); // needed to detect signOut in handleLocalStorageChange
    //     localStorage.clear();
    //     // Double-check removal (in case clear() didn't work in some browsers)
    //     if (localStorage.getItem(ESV_STORAGE_KEY)) {
    //       localStorage.removeItem(ESV_STORAGE_KEY);
    //     }
    //   }
    //   if (storageAvailable("sessionStorage")) {
    //     sessionStorage.removeItem(ESV_DETAILS); // needed to clear esv_details
    //   }
    // } else if (storageAvailable("sessionStorage")) {
    //   sessionStorage.clear();
    //   // Double-check removal (in case clear() didn't work in some browsers)
    //   if (sessionStorage.getItem(ESV_STORAGE_KEY)) {
    //     sessionStorage.removeItem(ESV_STORAGE_KEY);
    //   }
    // }

    if (storageAvailable("sessionStorage")) {
      sessionStorage.clear();
      // Double-check removal (in case clear() didn't work in some browsers)
      if (sessionStorage.getItem(ESV_STORAGE_KEY)) {
        sessionStorage.removeItem(ESV_STORAGE_KEY);
      }
    }

    // Also clear localStorage as a safety measure to remove any old data
    // (in case there's leftover data from before we switched to sessionStorage only)
    if (storageAvailable("localStorage")) {
      localStorage.removeItem(ESV_STORAGE_KEY);
      // Clear entire localStorage to ensure no leftover data
      try {
        localStorage.clear();
      } catch (e) {
        // If clear() fails, at least we tried to remove the specific key
        console.warn("Failed to clear localStorage:", e); // eslint-disable-line no-console
      }
    }

    /** Clear user title in state */
    const { setUserTitle } = appContext;
    if (setUserTitle && typeof setUserTitle === "function") {
      setUserTitle("");
    }

    // Dispatch custom event to notify App.js of logout (for same-tab reactivity)
    try {
      window.dispatchEvent(new Event("userLoggedOut"));
    } catch (e) {
      // Fallback for browsers that don't support Event constructor
      const event = document.createEvent("Event");
      event.initEvent("userLoggedOut", false, false);
      window.dispatchEvent(event);
    }

    // Navigate to appropriate login page
    try {
      if (isSupportUser) {
        history.replace("/login/support");
      } else {
        history.replace("/login");
      }
    } catch (e) {
      // Fallback if history.replace fails
      window.location.href = isSupportUser ? "/login/support" : "/login";
    }
  } finally {
    isSigningOut = false;
  }
};

/**
 * When local storage is updated, sync changes across browsing contexts
 * NOTE: Commented out since we're only using sessionStorage
 * @param {StorageEvent} event = {key, oldValue, newValue} key in local storage (only concerned w/ user_details), oldValue of key, newValue of key
 * @param {Object} history react-router history object
 * @param {Object} appContext app-level state
 */
// export const handleLocalStorageChange = ({ key, oldValue, newValue }, history, appContext) => {
//   if (key === ESV_STORAGE_KEY && checkIfRememberMe()) {
//     if (!newValue) { // other tab was signed out, sign out here
//       signOut(history, appContext);
//     } else if (!oldValue // other tab was signed in, reload page to sign in here
//       || JSON.parse(oldValue).company_name !== JSON.parse(newValue).company_name // other tab switched companies, refresh to switch here
//       || JSON.parse(oldValue).user_title !== JSON.parse(newValue).user_title) { // user role updated in other tab, refresh to update here
//       /** Reload the window */
//       window.location.reload(false);
//     }
//   }
// };

/**
 * Check if user exists, return user info if yes
 * @param {String} email
 * @return  {Object} { success, user_name, phone_number }
 */
export const checkUser = async (email, companyName) => {
  try {
    const userCredentials = getUserCredentials();
    const token = getCachedFirebaseToken() || userCredentials?.firebase_id_token;
    const config = {
      params: {
        email,
        company_name: companyName,
        disableInterceptor: true,
      },
    };
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` };
    }
    const resp = await axios.get(`${API_URL}/checkuser/`, config);
    const {
      status, user_name, user_contact, user_title, firebase_exist_status,
    } = resp.data.result;
    const result = {
      success: resp.data.message === "Success",
      message: resp.data.message,
      status,
      user_name,
      phone_number: user_contact,
      user_title,
      firebase_exist_status,
    };

    return result;
  } catch (e) {
    const message = e.response?.data?.message || "Something went wrong.";
    return { success: false, message, userNotFound: message === "User not found" };
  }
};

/**
 * When page is loaded, call the check user api and update user_title
 * @param {Object} appContext app-level state
 */
export const handlePageLoad = async (appContext) => {
  if (isUser()) {
    const userInfo = getUserCredentials();
    const isSupportUser = userInfo.uid === "support" || userInfo.user_email === "support";
    if (!isSupportUser) { // user_title will always be admin if logged in through support login
      const { success, user_title } = await checkUser(userInfo.user_email, userInfo.company_name);
      if (success && user_title !== userInfo.user_title) {
        setUserCredentials(null, { ...userInfo, user_title }, appContext);
      }
    }
  }
};

/**
 * Get list of tabs
 * @returns Array of tabs
 */
export const getTabsList = () => {
  const config = sessionStorage.getItem(ESV_DETAILS);
  if (!config) return [];
  const features = JSON.parse(config)?.tabsList;
  return features ?? [];
};

/**
 * Get list of features
 * @returns Array of features
 */
export const getFeatures = () => {
  const config = sessionStorage.getItem(ESV_DETAILS);
  if (!config) return [];
  const features = JSON.parse(config)?.featuresList;
  return features ?? [];
};

/**
 * Checks if company has a specific feature.
 * @param {string} feature name of the feature
 * @returns {boolean} if feature exists returns true otherwise false
 */
export const hasFeature = (feature) => {
  const features = getFeatures();
  return features.indexOf(feature) !== -1;
};

/**
 * Get app version
 * @returns {string} app version
 */
export const getAppVersion = () => version;

/**
 * Get the current year
 * @returns {number} current year
 */
export const getCurrentYear = () => moment().year();

// /**
//  * Checks is the customer has permission for the feature
//  * @param {*} user user details
//  * @param {string} permission permission name/key;
//  * @returns {boolean}
//  */
// export const checkCustomerPermission = (user, permission) => {
//   const customerList = hardcodedCustomerPermissions[permission] || [];
//   return customerList.includes(user?.company_domain.toLowerCase());
// };

/**
 * Get esv_details from session storage.
 * @returns {Object} parsed esv_details object, or {} if missing/invalid
 */
export const getEsvDetails = () => {
  const esvDetails = sessionStorage.getItem(ESV_DETAILS);
  if (!esvDetails) return {};
  try {
    const parsed = JSON.parse(esvDetails);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

/**
 * Merge fields into esv_details in session storage.
 * @param {Object} partial fields to merge
 * @returns {Object} updated esv_details object
 */
export const updateEsvDetails = (partial = {}) => {
  const nextDetails = {
    ...getEsvDetails(),
    ...partial,
  };
  sessionStorage.setItem(ESV_DETAILS, JSON.stringify(nextDetails));
  return nextDetails;
};

/**
 * Normalize customer_type from /useraccounts/.
 * null / undefined / empty → "normal".
 * @param {string|null|undefined} customerType
 * @returns {string}
 */
export const normalizeCustomerType = (customerType) => {
  const type = String(customerType ?? "").trim().toLowerCase();
  if (!type || type === "null" || type === "normal") {
    return "normal";
  }
  return type;
};

/**
 * True when invite and add-company should follow the normal path.
 * Postpay is included; prepaid and subscription are not.
 * @param {string|null|undefined} customerType
 * @returns {boolean}
 */
export const isNormalOrPostpayCustomer = (customerType) => {
  const type = normalizeCustomerType(customerType);
  return type === "normal" || type === "postpay";
};

/**
 * Subscription and postpay cannot add a company. They contact their CSM.
 * @param {string|null|undefined} customerType
 * @returns {boolean}
 */
export const isAddCompanyBlocked = (customerType) => {
  const type = normalizeCustomerType(customerType);
  return type === "subscription" || type === "postpay";
};

/**
 * Get customer config from session storage (now stored in esv_details)
 * @returns {Object|null} customer config object or null if not found
 */
export const getCustomerConfig = () => {
  const customerConfig = getEsvDetails().customer_config || null;
  if (!customerConfig) return null;
  return {
    ...customerConfig,
    customer_type: normalizeCustomerType(customerConfig.customer_type),
  };
};

/**
 * Get DMS config from customer config
 * @returns {Object|null} DMS configuration or null if not found
 */
export const getDMSConfig = () => {
  const customerConfig = getCustomerConfig();
  return customerConfig?.feature_config?.DMS || null;
};

/**
 * Get specific feature config by name
 * @param {string} featureName - Name of the feature (e.g., "DMS", "NCR", "MOC")
 * @returns {Object|null} feature configuration or null if not found
 */
export const getFeatureConfig = (featureName) => {
  const customerConfig = getCustomerConfig();
  return customerConfig?.feature_config?.[featureName] || null;
};
/*
 * Transforms account_permissions array from API response into a flat object.
 * If a permission code is present in the array, it means the user has that permission (enabled).
 * API Format: [{ title: "Product", permissions: [{ code: "product_generate_partial" }, { code: "product_analytics" }] }, ...]
 * Output Format: { "product_generate_partial": true, "product_analytics": true, ... }
 *
 * @param {Array} accountPermissions - The account_permissions array from API response
 * @returns {Object} - Flat object with permission codes as keys (all set to true if present)
 */
export const transformAccountPermissions = (accountPermissions) => {
  if (!Array.isArray(accountPermissions)) {
    return {};
  }

  const flattened = {};

  accountPermissions.forEach((section) => {
    if (section.permissions && Array.isArray(section.permissions)) {
      section.permissions.forEach((permission) => {
        if (permission.code) {
          flattened[permission.code] = true;
        }
      });
    }
  });

  return flattened;
};

// Allow only alphabets, numbers, spaces, underscores and hyphens
export const BLOCKED_KEY_REGEX = /[^a-zA-Z0-9 _-]/;
export const ALLOWED_CHAR_REGEX = /^[a-zA-Z0-9 _-]*$/;
